import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import stats from '../../models/stats';
import moment from 'moment';

const defaultFilters = {
  from: moment.utc().startOf('day').toISOString(),
  to: moment.utc().endOf('day').toISOString(),
};

const formatCompactNumber = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '';
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
};

export default function MetricChart({
  title,
  type = 'totalAmount',
  fullPage = false,
  currencySymbol = '€',
  extraFilters,
  filters: externalFilters,
  height = 240,
}) {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [localFilters, setLocalFilters] = useState(defaultFilters);
  const [error, setError] = useState('');

  const filters = externalFilters || localFilters;
  // keep setter for future embedded filters; currently charts are driven by parent filters
  void setLocalFilters;

  const mergedFilters = useMemo(() => {
    return { ...filters, ...(extraFilters || {}), type };
  }, [filters, extraFilters, type]);

  useEffect(() => {
    let mounted = true;
    setError('');
    stats.getStats(mergedFilters).then((statsData) => {
      if (!mounted) return;
      if (!statsData || !Array.isArray(statsData.stats)) {
        setData([]);
        setError('No data (unsupported metric type on backend?)');
        return;
      }
      setData(stats.buildData(statsData.stats, statsData.interval, statsData.from, statsData.to, statsData.subunit));
    }).catch(() => {
      if (!mounted) return;
      setData([]);
      setError('Failed to load stats');
    });
    return () => { mounted = false; };
  }, [mergedFilters]);

  return (
      <Box sx={{ width: '100%' }}>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {title}
              </Typography>
              {error ? (
                  <Typography variant="caption" color="text.secondary">
                      {error}
                  </Typography>
              ) : null}
          </Stack>

          <ResponsiveContainer width="100%" height={height}>
              <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                  <defs>
                      <linearGradient id={`grad-${type}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.22} />
                          <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                      </linearGradient>
                  </defs>
                  <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="time" tick={false} axisLine={false} />
                  <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => formatCompactNumber(v)}
                      style={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      width={56}
                      tickMargin={8}
                  />
                  <Tooltip
                      formatter={(value) => {
                          if (type.toLowerCase().includes('count')) return [value, 'count'];
                          return [`${value}${currencySymbol}`, 'amount'];
                      }}
                      contentStyle={{
                          borderRadius: 12,
                          borderColor: theme.palette.divider,
                          background: theme.palette.background.paper,
                      }}
                  />
                  <Area
                      isAnimationActive={false}
                      type="monotone"
                      dataKey={type.toLowerCase().includes('count') ? 'count' : 'amount'}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      fill={`url(#grad-${type})`}
                      dot={false}
                  />
              </AreaChart>
          </ResponsiveContainer>
      </Box>
  );
}


